import { useCallback, useEffect, useState } from 'react';
import { ExternalLink, ChevronLeft, ChevronRight, Clock, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import useEmblaCarousel from 'embla-carousel-react';
import { projects } from '../../data/projects';
import { useInView } from '../../hooks/useInView';
import { TiltCard } from '../effects/TiltCard';

const ProjectSlide = ({ project }: { project: typeof projects[0] }) => (
  <div className="flex-[0_0_100%] sm:flex-[0_0_50%] lg:flex-[0_0_33.333%] pl-6">
    <TiltCard max={5} className="rounded-2xl h-full">
    <div className="group relative bg-card rounded-2xl shadow-card border border-border hover:border-primary/15 hover:shadow-elevated transition-all duration-500 flex flex-col overflow-hidden h-full">

      {/* Gradient header */}
      <div className={`bg-gradient-to-br ${project.accent} px-6 pt-6 pb-5 relative min-h-[120px]`}>
        {project.comingSoon ? (
          <span className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-card/90 backdrop-blur-sm border border-border text-muted-foreground text-xs font-semibold whitespace-nowrap shadow-sm">
            <Clock size={10} />
            Coming Soon
          </span>
        ) : (
          <span className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/15 backdrop-blur-sm border border-primary/25 text-primary text-xs font-semibold whitespace-nowrap shadow-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
            Live
          </span>
        )}

        <p className={`text-[10px] font-bold uppercase tracking-[0.15em] ${project.accentText} mb-2 opacity-80 pr-16 leading-tight`}>
          {project.category}
        </p>
        <h3
          className="text-xl font-bold text-foreground leading-tight"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {project.name}
        </h3>
        <p className="text-xs text-muted-foreground mt-1 font-medium">{project.role}</p>
      </div>

      {/* Body */}
      <div className="flex flex-col flex-1 px-6 py-5 gap-5">

        {/* Quick stats chips */}
        <div className="flex flex-wrap gap-2">
          {project.stats.map((stat) => (
            <div
              key={stat.label}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-muted/50 border border-border"
            >
              <span className="text-[10px] text-muted-foreground">{stat.label}:</span>
              <span className="text-[10px] font-semibold text-foreground">{stat.value}</span>
            </div>
          ))}
        </div>

        <p className="text-sm text-muted-foreground leading-relaxed flex-1 line-clamp-4">
          {project.description}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-2">
          {project.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="px-2.5 py-1 rounded-lg bg-muted/50 text-muted-foreground text-[11px] font-medium border border-border"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Tech stack */}
        {project.techStack && project.techStack.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {project.techStack.map((tech) => (
              <span
                key={tech}
                className="px-2 py-0.5 rounded-md bg-primary/5 text-muted-foreground/80 text-[10px] font-mono border border-border/60"
              >
                {tech}
              </span>
            ))}
          </div>
        )}

        {/* CTAs */}
        <div className="flex flex-col gap-2.5 mt-auto pt-2">
          {project.comingSoon ? (
            <span className="inline-flex items-center gap-2 justify-center w-full py-3 rounded-xl text-sm font-semibold bg-muted text-muted-foreground border border-border cursor-not-allowed">
              <Clock size={14} />
              Launching Soon
            </span>
          ) : (
            <>
              <a
                href={project.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2.5 justify-center w-full py-3 rounded-xl text-sm font-semibold gradient-premium text-primary-foreground hover:opacity-95 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300 shadow-card hover:shadow-hero"
              >
                Visit Project
                <ExternalLink size={14} />
              </a>
              <Link
                to="/projects"
                className="inline-flex items-center gap-2 justify-center w-full py-3 rounded-xl text-sm font-semibold border border-border text-muted-foreground hover:text-foreground hover:border-primary/30 transition-all duration-200"
              >
                View Case Study
                <ArrowRight size={14} />
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
    </TiltCard>
  </div>
);

export const Projects = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'start' });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);
  const { ref: headRef, inView: headIn } = useInView();

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((index: number) => emblaApi?.scrollTo(index), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    onSelect();
    return () => { emblaApi.off('select', onSelect); };
  }, [emblaApi, onSelect]);

  return (
    <section id="projects" className="py-20 sm:py-24 md:py-28 px-4 sm:px-6 lg:px-8 bg-background relative">
      <div className="max-w-6xl mx-auto">

        {/* Section header */}
        <div
          ref={headRef}
          className={`flex flex-col sm:flex-row sm:items-end justify-between mb-10 sm:mb-12 md:mb-14 gap-5 sm:gap-6 transition-all duration-700 ${headIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div>
            <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.25em] text-accent mb-3 md:mb-4">Ventures</p>
            <h2
              className="text-3xl sm:text-4xl md:text-4xl lg:text-5xl font-bold text-foreground"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Healthcare Projects
            </h2>
            <div className="mt-5 md:mt-6 w-16 h-1 bg-gradient-to-r from-primary to-accent rounded-full" />
          </div>

          <Link
            to="/projects"
            className="inline-flex items-center gap-2.5 px-5 sm:px-6 py-2.5 sm:py-3 rounded-xl border border-border text-sm font-semibold text-muted-foreground hover:text-primary hover:border-primary/30 transition-all duration-200 self-start sm:self-end"
          >
            View All Case Studies
            <ArrowRight size={16} />
          </Link>
        </div>

        {/* Carousel */}
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex -ml-6">
            {projects.map((project) => (
              <ProjectSlide key={project.slug} project={project} />
            ))}
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between mt-10">
          {/* Dot indicators */}
          <div className="flex items-center gap-2.5">
            {projects.map((_, idx) => (
              <button
                key={idx}
                onClick={() => scrollTo(idx)}
                className={`rounded-full transition-all duration-300 ${
                  idx === selectedIndex
                    ? 'w-8 h-2.5 gradient-premium'
                    : 'w-2.5 h-2.5 bg-border hover:bg-muted-foreground'
                }`}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>

          {/* Arrow buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={scrollPrev}
              disabled={!canScrollPrev}
              className="w-11 h-11 rounded-xl border border-border bg-card flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/30 hover:shadow-md disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
              aria-label="Previous project"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={scrollNext}
              disabled={!canScrollNext}
              className="w-11 h-11 rounded-xl border border-border bg-card flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/30 hover:shadow-md disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
              aria-label="Next project"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
